import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/QuickBooks.css';

// Feature flag: QB integration hidden until paid
const QB_FEATURE_ENABLED = false;

const QuickBooks = () => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('customers');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // MK entities
  const [mkCustomers, setMkCustomers] = useState([]);
  const [mkItems, setMkItems] = useState([]);

  // QB entities (cached from sync)
  const [qbCustomers, setQbCustomers] = useState([]);
  const [qbItems, setQbItems] = useState([]);

  // Mappings
  const [customerMappings, setCustomerMappings] = useState([]);
  const [itemMappings, setItemMappings] = useState([]);

  // Unsaved changes tracking
  const [pendingCustomerMappings, setPendingCustomerMappings] = useState({});
  const [pendingItemMappings, setPendingItemMappings] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Search
  const [customerSearch, setCustomerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');

  // Filter
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);

  // Dropdown states
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownSearches, setDropdownSearches] = useState({});

  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/dashboard';
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        mkCustRes, mkItemsRes,
        qbCustRes, qbItemsRes,
        custMapRes, itemMapRes
      ] = await Promise.all([
        api.get('/customers'),
        api.get('/items'),
        api.get('/qb/customers'),
        api.get('/qb/items'),
        api.get('/qb/mappings/customers'),
        api.get('/qb/mappings/items')
      ]);

      setMkCustomers(mkCustRes.data || []);
      setMkItems(mkItemsRes.data || []);
      setQbCustomers(qbCustRes.data || []);
      setQbItems(qbItemsRes.data || []);
      setCustomerMappings(custMapRes.data || []);
      setItemMappings(itemMapRes.data || []);
    } catch (err) {
      showMessage('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const getCustomerMapping = (customerId) => {
    return customerMappings.find(m => m.customer_id === customerId);
  };

  const getItemMapping = (itemId) => {
    return itemMappings.find(m => m.item_id === itemId);
  };

  const handleCustomerMappingChange = (customerId, qbListId) => {
    setPendingCustomerMappings(prev => ({
      ...prev,
      [customerId]: qbListId
    }));
    setHasUnsavedChanges(true);
    setActiveDropdown(null);
  };

  const handleItemMappingChange = (itemId, qbListId) => {
    setPendingItemMappings(prev => ({
      ...prev,
      [itemId]: qbListId
    }));
    setHasUnsavedChanges(true);
    setActiveDropdown(null);
  };

  const handleSaveAllMappings = async () => {
    setLoading(true);
    try {
      const customerPromises = Object.entries(pendingCustomerMappings).map(([customerId, qbListId]) =>
        api.post('/qb/mappings/customers', {
          customer_id: parseInt(customerId),
          qb_list_id: qbListId
        })
      );

      const itemPromises = Object.entries(pendingItemMappings).map(([itemId, qbListId]) =>
        api.post('/qb/mappings/items', {
          item_id: parseInt(itemId),
          qb_list_id: qbListId
        })
      );

      await Promise.all([...customerPromises, ...itemPromises]);

      showMessage('success', 'All mappings saved successfully');
      setPendingCustomerMappings({});
      setPendingItemMappings({});
      setHasUnsavedChanges(false);
      await loadData();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelChanges = () => {
    setPendingCustomerMappings({});
    setPendingItemMappings({});
    setHasUnsavedChanges(false);
    setActiveDropdown(null);
  };

  const getCurrentMapping = (type, id) => {
    if (type === 'customer') {
      if (pendingCustomerMappings[id] !== undefined) {
        return pendingCustomerMappings[id];
      }
      return getCustomerMapping(id)?.qb_list_id || '';
    } else {
      if (pendingItemMappings[id] !== undefined) {
        return pendingItemMappings[id];
      }
      return getItemMapping(id)?.qb_list_id || '';
    }
  };

  const hasChanges = (type, id) => {
    if (type === 'customer') {
      return pendingCustomerMappings[id] !== undefined;
    } else {
      return pendingItemMappings[id] !== undefined;
    }
  };

  // Filter customers
  const filteredCustomers = mkCustomers.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(customerSearch.toLowerCase());
    const mapping = getCustomerMapping(c.id);
    const matchesFilter = !showUnmappedOnly || !mapping;
    return matchesSearch && matchesFilter;
  });

  // Filter items
  const filteredItems = mkItems.filter(i => {
    const matchesSearch =
      i.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
      i.part_number?.toLowerCase().includes(itemSearch.toLowerCase());
    const mapping = getItemMapping(i.id);
    const matchesFilter = !showUnmappedOnly || !mapping;
    return matchesSearch && matchesFilter;
  });

  // Stats
  const mappedCustomersCount = customerMappings.length;
  const unmappedCustomersCount = mkCustomers.length - mappedCustomersCount;
  const mappedItemsCount = itemMappings.length;
  const unmappedItemsCount = mkItems.length - mappedItemsCount;

  if (loading && QB_FEATURE_ENABLED) {
    return (
      <div className="qb-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  // Show "Coming Soon" when feature is disabled
  if (!QB_FEATURE_ENABLED) {
    return (
      <div className="qb-page">
        <div className="page-header">
          <h1>QuickBooks Integration</h1>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📗</div>
          <h2 style={{ fontSize: '28px', marginBottom: '12px', color: '#333' }}>Coming Soon</h2>
          <p style={{ fontSize: '16px', color: '#666', maxWidth: '400px' }}>
            QuickBooks Desktop integration is available as an add-on feature.
            Contact us to enable automatic invoice creation and data sync.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="qb-page">
      <div className="page-header">
        <div className="header-row">
          <h1>QuickBooks Mappings</h1>
          {hasUnsavedChanges && (
            <div className="save-actions">
              <button className="btn-cancel" onClick={handleCancelChanges}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveAllMappings} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {hasUnsavedChanges && (
        <div className="unsaved-notice">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zm0-6H7V4h2v2z"/>
          </svg>
          <span>You have unsaved changes. Click "Save Changes" to apply them.</span>
        </div>
      )}

      <div className="qb-stats-bar">
        <div className="qb-stat-item">
          <span className="stat-label">QB Customers:</span>
          <span className="stat-value">{qbCustomers.length}</span>
        </div>
        <div className="qb-stat-item">
          <span className="stat-label">QB Items:</span>
          <span className="stat-value">{qbItems.length}</span>
        </div>
        <div className="qb-stat-item mapped">
          <span className="stat-label">Mapped Customers:</span>
          <span className="stat-value">{mappedCustomersCount}/{mkCustomers.length}</span>
        </div>
        <div className="qb-stat-item mapped">
          <span className="stat-label">Mapped Items:</span>
          <span className="stat-value">{mappedItemsCount}/{mkItems.length}</span>
        </div>
      </div>

      {qbCustomers.length === 0 && qbItems.length === 0 && (
        <div className="qb-empty-notice">
          <p>No QuickBooks data synced yet.</p>
          <p>Go to Settings → QuickBooks → Queue Full Sync, then connect Web Connector.</p>
        </div>
      )}

      <div className="qb-tabs">
        <button
          className={`qb-tab ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers ({unmappedCustomersCount} unmapped)
        </button>
        <button
          className={`qb-tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          Items ({unmappedItemsCount} unmapped)
        </button>
      </div>

      <div className="qb-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={activeTab === 'customers' ? 'Search customers...' : 'Search items...'}
            value={activeTab === 'customers' ? customerSearch : itemSearch}
            onChange={(e) => activeTab === 'customers'
              ? setCustomerSearch(e.target.value)
              : setItemSearch(e.target.value)
            }
          />
        </div>
        <label className="filter-toggle">
          <input
            type="checkbox"
            checked={showUnmappedOnly}
            onChange={(e) => setShowUnmappedOnly(e.target.checked)}
          />
          Show unmapped only
        </label>
      </div>

      {activeTab === 'customers' && (
        <div className="mapping-table-container">
          <table className="mapping-table">
            <thead>
              <tr>
                <th>MK Customer</th>
                <th>QuickBooks Customer</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => {
                const currentMapping = getCurrentMapping('customer', customer.id);
                const hasChange = hasChanges('customer', customer.id);
                const dropdownKey = `customer-${customer.id}`;
                const isDropdownOpen = activeDropdown === dropdownKey;
                const searchTerm = dropdownSearches[dropdownKey] || '';
                const filteredQbCustomers = qbCustomers.filter(qc =>
                  qc.name.toLowerCase().includes(searchTerm.toLowerCase())
                );
                const selectedQb = qbCustomers.find(qc => qc.list_id === currentMapping);

                return (
                  <tr key={customer.id} className={hasChange ? 'row-modified' : ''}>
                    <td className="entity-name">{customer.name}</td>
                    <td>
                      <div className="mapping-dropdown-container">
                        <div className="unified-search-box">
                          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                          </svg>
                          <input
                            type="text"
                            className="mapping-search-input"
                            placeholder={selectedQb ? selectedQb.name : "Search QB customer..."}
                            value={searchTerm}
                            onChange={(e) => {
                              setDropdownSearches(prev => ({
                                ...prev,
                                [dropdownKey]: e.target.value
                              }));
                              setActiveDropdown(dropdownKey);
                            }}
                            onFocus={() => setActiveDropdown(dropdownKey)}
                          />
                          <button
                            type="button"
                            className="dropdown-toggle"
                            onClick={() => setActiveDropdown(isDropdownOpen ? null : dropdownKey)}
                          >
                            ▼
                          </button>
                        </div>
                        {isDropdownOpen && (
                          <div className="mapping-dropdown">
                            <div
                              className="dropdown-item"
                              onClick={() => handleCustomerMappingChange(customer.id, '')}
                            >
                              <span className="dropdown-name">(Clear mapping)</span>
                            </div>
                            {filteredQbCustomers.length === 0 ? (
                              <div className="dropdown-empty">No customers found</div>
                            ) : (
                              filteredQbCustomers.slice(0, 50).map(qc => (
                                <div
                                  key={qc.list_id}
                                  className="dropdown-item"
                                  onClick={() => handleCustomerMappingChange(customer.id, qc.list_id)}
                                >
                                  <span className="dropdown-name">{qc.name}</span>
                                </div>
                              ))
                            )}
                            {filteredQbCustomers.length > 50 && (
                              <div className="dropdown-empty">
                                Showing 50 of {filteredQbCustomers.length} — type to search
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {currentMapping ? (
                        <span className="status-badge mapped">
                          {hasChange ? 'Modified' : 'Mapped'}
                        </span>
                      ) : (
                        <span className="status-badge unmapped">Unmapped</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredCustomers.length === 0 && (
            <div className="empty-state">No customers found</div>
          )}
        </div>
      )}

      {activeTab === 'items' && (
        <div className="mapping-table-container">
          <table className="mapping-table">
            <thead>
              <tr>
                <th>MK Item</th>
                <th>Part #</th>
                <th>QuickBooks Item</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const currentMapping = getCurrentMapping('item', item.id);
                const hasChange = hasChanges('item', item.id);
                const dropdownKey = `item-${item.id}`;
                const isDropdownOpen = activeDropdown === dropdownKey;
                const searchTerm = dropdownSearches[dropdownKey] || '';
                const filteredQbItems = qbItems.filter(qi =>
                  qi.name.toLowerCase().includes(searchTerm.toLowerCase())
                );
                const selectedQb = qbItems.find(qi => qi.list_id === currentMapping);

                return (
                  <tr key={item.id} className={hasChange ? 'row-modified' : ''}>
                    <td className="entity-name">{item.name}</td>
                    <td className="part-number">{item.part_number}</td>
                    <td>
                      <div className="mapping-dropdown-container">
                        <div className="unified-search-box">
                          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="M21 21l-4.35-4.35"/>
                          </svg>
                          <input
                            type="text"
                            className="mapping-search-input"
                            placeholder={selectedQb ? selectedQb.name : "Search QB item..."}
                            value={searchTerm}
                            onChange={(e) => {
                              setDropdownSearches(prev => ({
                                ...prev,
                                [dropdownKey]: e.target.value
                              }));
                              setActiveDropdown(dropdownKey);
                            }}
                            onFocus={() => setActiveDropdown(dropdownKey)}
                          />
                          <button
                            type="button"
                            className="dropdown-toggle"
                            onClick={() => setActiveDropdown(isDropdownOpen ? null : dropdownKey)}
                          >
                            ▼
                          </button>
                        </div>
                        {isDropdownOpen && (
                          <div className="mapping-dropdown">
                            <div
                              className="dropdown-item"
                              onClick={() => handleItemMappingChange(item.id, '')}
                            >
                              <span className="dropdown-name">(Clear mapping)</span>
                            </div>
                            {filteredQbItems.length === 0 ? (
                              <div className="dropdown-empty">No items found</div>
                            ) : (
                              filteredQbItems.slice(0, 50).map(qi => (
                                <div
                                  key={qi.list_id}
                                  className="dropdown-item"
                                  onClick={() => handleItemMappingChange(item.id, qi.list_id)}
                                >
                                  <span className="dropdown-name">{qi.name}</span>
                                </div>
                              ))
                            )}
                            {filteredQbItems.length > 50 && (
                              <div className="dropdown-empty">
                                Showing 50 of {filteredQbItems.length} — type to search
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {currentMapping ? (
                        <span className="status-badge mapped">
                          {hasChange ? 'Modified' : 'Mapped'}
                        </span>
                      ) : (
                        <span className="status-badge unmapped">Unmapped</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && (
            <div className="empty-state">No items found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickBooks;
